import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Twitter, Linkedin, Youtube, Mail, Phone, MapPin, MessageCircle } from 'lucide-react';
import { NAV_CATEGORIES, SOCIAL_LINKS } from '../mock';
import { useLanguage } from '../i18n/LanguageContext';
import LanguageToggle from './LanguageToggle';

const SOCIALS = [
  { Icon: Facebook,  href: SOCIAL_LINKS.facebook,  label: 'Facebook' },
  { Icon: Instagram, href: SOCIAL_LINKS.instagram, label: 'Instagram' },
  { Icon: Twitter,   href: SOCIAL_LINKS.twitter,   label: 'Twitter / X' },
  { Icon: Linkedin,  href: SOCIAL_LINKS.linkedin,  label: 'LinkedIn' },
  { Icon: Youtube,   href: SOCIAL_LINKS.youtube,   label: 'YouTube' },
  { Icon: MapPin,    href: SOCIAL_LINKS.google,    label: 'Google Business' },
];

const Footer = () => {
  const { t } = useLanguage();
  const catLabel = (cat) => t(`category.${cat.category}`, cat.label);

  return (
    <footer className="bg-[#0f2540] text-gray-300">
      <div className="max-w-[1500px] mx-auto px-4 md:px-8 xl:px-10 2xl:px-14 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 xl:gap-12">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#14A098] to-[#5eead4] flex items-center justify-center">
              <span className="text-white font-bold text-xl">S</span>
            </div>
            <div className="leading-tight">
              <div className="font-extrabold text-white text-lg">SOLVE <span className="text-[#14A098]">TAX</span></div>
              <div className="text-[10px] text-gray-400 uppercase tracking-wider">{t('nav.tagline', 'Your Finances, Our Priority')}</div>
            </div>
          </div>
          <p className="text-sm leading-relaxed text-gray-400 mb-5 max-w-[440px]">
            {t('footer.blurb', 'Trusted income tax filing, GST registration, company incorporation and accounting services across India. Expert CAs, transparent pricing, on-time delivery.')}
          </p>
          <div className="flex flex-wrap gap-3">
            {SOCIALS.map(({ Icon, href, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                title={label}
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#14A098] flex items-center justify-center transition-colors"
              >
                <Icon className="w-4 h-4 text-white" />
              </a>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2">
          <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-wider">{t('footer.quick_links', 'Quick Links')}</h4>
          <ul className="space-y-3 text-sm">
            {[
              ['/', t('footer.home', 'Home')],
              ['/about', t('footer.about_us', 'About Us')],
              ['/contact', t('footer.contact', 'Contact')],
              ['/referral', t('footer.referral', 'Referral')],
              ['/privacy', t('footer.privacy', 'Privacy Policy')],
            ].map(([to, label]) => (
              <li key={to}><Link to={to} className="hover:text-[#14A098] transition-colors">{label}</Link></li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-2">
          <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-wider">{t('footer.top_services', 'Top Services')}</h4>
          <ul className="space-y-3 text-sm">
            {NAV_CATEGORIES.map((cat) => (
              <li key={cat.category}>
                <Link to={`/services/${cat.category.toLowerCase()}`} className="hover:text-[#14A098] transition-colors">{catLabel(cat)}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider">{t('footer.support', 'Support')}</h4>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-gray-400 whitespace-nowrap">{t('footer.language', 'Language')}</span>
              <LanguageToggle variant="dark" />
            </div>
          </div>
          <ul className="space-y-4 text-sm">
            <li className="flex gap-3">
              <MapPin className="w-4 h-4 text-[#14A098] mt-1 shrink-0" />
              <span>
                Flat No 302, 2nd floor, Pruthivi Apartment, Street No. 8, Habsiguda, Secunderabad - 500007
                <br />
                <a
                  href="https://maps.app.goo.gl/ztVdHda9nmw9Bc5r6"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#14A098] underline"
                >
                  {t('footer.view_maps', 'View on Google Maps')}
                </a>
              </span>
            </li>
            <li className="flex gap-3 items-center">
              <Mail className="w-4 h-4 text-[#14A098] shrink-0" />
              <a href="mailto:contact@solvetax.com" className="hover:text-[#14A098]">contact@solvetax.com</a>
            </li>
            <li className="flex gap-3 items-center">
              <Phone className="w-4 h-4 text-[#14A098] shrink-0" />
              <a href="tel:+919346004430" className="hover:text-[#14A098]">+91 93460 04430</a>
            </li>
            <li className="flex gap-3 items-center">
              <MessageCircle className="w-4 h-4 text-[#14A098] shrink-0" />
              <a href="https://wa.me/919346004430" target="_blank" rel="noopener noreferrer" className="hover:text-[#14A098]">{t('footer.whatsapp_chat', 'WhatsApp Chat')}</a>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-[1500px] mx-auto px-4 md:px-8 xl:px-10 2xl:px-14 py-6 flex flex-col md:flex-row justify-between items-center text-xs text-gray-400 gap-2">
          <span>{t('footer.copyright', 'SOLVETAX © 2025. All rights reserved.')}</span>
          <span>{t('footer.tagline', 'Crafted with care for Indian taxpayers • GSTIN-ready • ICAI-aligned advisors')}</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
